// ═══════════════════════════════════════════════════════════════
// PIPELINE PULSE WIDGET
// Path: src/components/dashboard/PipelinePulse.jsx
//
// Compact cultivation arc view:
//   - Pathway distribution (how many in each stage)
//   - Stage health indicators (avg days since contact per stage)
//   - Click any stage to navigate to cultivation pipeline
//
// Grounded in Four P's: Pathways tell you where donors sit
// in the cultivation arc. This widget answers "is our pipeline
// healthy?" at a glance.
// ═══════════════════════════════════════════════════════════════

import React, { useMemo } from "react";
import { Compass, ArrowRight, X } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { calculateEngagement, getMemberActivities } from "../../utils/engagementScoring";

export const PipelinePulse = ({ onClose } = {}) => {
  const { palette, members, pathways, navigate, allGifts, allEventAttendance, allSponsorships } = useApp();

  const stages = useMemo(() => {
    const stageMap = {};

    pathways.forEach((p) => {
      stageMap[p.id] = {
        id: p.id,
        name: p.name,
        color: p.color,
        count: 0,
        totalScore: 0,
        totalDays: 0,
        daysCount: 0, // members with known days
        noContact60: 0, // members with 60+ days since contact
      };
    });

    members.forEach((m) => {
      const pw = m.pathway || "unengaged";
      if (!stageMap[pw]) return;

      stageMap[pw].count++;
      const engagement = calculateEngagement(m, undefined, getMemberActivities(m.id, allGifts, allEventAttendance, allSponsorships));
      stageMap[pw].totalScore += engagement.score;

      if (engagement.daysSinceContact !== null) {
        stageMap[pw].totalDays += engagement.daysSinceContact;
        stageMap[pw].daysCount++;
        if (engagement.daysSinceContact >= 60) {
          stageMap[pw].noContact60++;
        }
      }
    });

    return pathways.map((p) => {
      const s = stageMap[p.id];
      return {
        ...s,
        avgScore: s.count > 0 ? Math.round(s.totalScore / s.count) : 0,
        avgDays: s.daysCount > 0 ? Math.round(s.totalDays / s.daysCount) : null,
        pctStale: s.count > 0 ? Math.round((s.noContact60 / s.count) * 100) : 0,
      };
    }).filter((s) => s.count > 0);
  }, [members, pathways]);

  const totalMembers = stages.reduce((s, st) => s + st.count, 0);

  // Health color based on % stale
  const getHealthColor = (pctStale) => {
    if (pctStale <= 20) return "#16a34a";
    if (pctStale <= 50) return "#f59e0b";
    return "#dc2626";
  };

  return (
    <div
      style={{
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 10,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Compass size={ICON_SIZES.sm} style={{ color: "#10b981" }} />
          <span
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 700,
              color: palette.text1,
              fontFamily: FONT_FAMILY,
            }}
          >
            Pipeline Pulse
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              fontSize: "0.6rem",
              color: palette.text3,
              fontFamily: FONT_FAMILY,
            }}
          >
            {totalMembers} in pipeline
          </span>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 2,
                display: "flex",
                alignItems: "center",
              }}
            >
              <X size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
            </button>
          )}
        </div>
      </div>

      {/* Horizontal distribution bar */}
      <div
        style={{
          display: "flex",
          height: 10,
          borderRadius: 5,
          overflow: "hidden",
          background: `${palette.text3}10`,
        }}
      >
        {stages.map((s) => (
          <div
            key={s.id}
            style={{
              width: `${totalMembers > 0 ? (s.count / totalMembers) * 100 : 0}%`,
              background: s.color,
              transition: "width 0.3s",
            }}
            title={`${s.name}: ${s.count}`}
          />
        ))}
      </div>

      {/* Stage cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {stages.map((s) => {
          const healthColor = getHealthColor(s.pctStale);
          return (
            <div
              key={s.id}
              onClick={() => navigate("cultivation")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 8px",
                borderRadius: 6,
                cursor: "pointer",
                transition: "background 0.1s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = `${s.color}08`}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              {/* Color dot */}
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  background: s.color,
                  flexShrink: 0,
                }}
              />

              {/* Stage name + count */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 600,
                    color: palette.text1,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {s.name}
                </div>
              </div>

              {/* Count */}
              <span
                style={{
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 700,
                  color: s.color,
                  fontFamily: FONT_FAMILY,
                  minWidth: 28,
                  textAlign: "right",
                }}
              >
                {s.count}
              </span>

              {/* Avg days */}
              <span
                style={{
                  fontSize: "0.6rem",
                  color: palette.text3,
                  fontFamily: FONT_FAMILY,
                  minWidth: 35,
                  textAlign: "right",
                }}
                title="Average days since last contact"
              >
                {s.avgDays !== null ? `${s.avgDays}d` : "—"}
              </span>

              {/* Health indicator */}
              <div
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: healthColor,
                  flexShrink: 0,
                }}
                title={`${s.pctStale}% with no contact in 60+ days`}
              />
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div
        style={{
          display: "flex",
          gap: 12,
          fontSize: "0.55rem",
          color: palette.text3,
          fontFamily: FONT_FAMILY,
          borderTop: `1px solid ${palette.cardBorder}`,
          paddingTop: 6,
        }}
      >
        <span>Count</span>
        <span>Avg days since contact</span>
        <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <div style={{ width: 5, height: 5, borderRadius: 3, background: "#16a34a" }} />
          {"< 20% stale"}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <div style={{ width: 5, height: 5, borderRadius: 3, background: "#f59e0b" }} />
          {"20-50%"}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <div style={{ width: 5, height: 5, borderRadius: 3, background: "#dc2626" }} />
          {"> 50% stale"}
        </div>
      </div>
    </div>
  );
};
